import type { CellClassParams, ColDef } from 'ag-grid-community'
import type { ApprovalType, GuidanceType } from '../components/ComparisonTabs'
import { DYNAMIC_COLORS, MAX_DYNAMIC_COLUMNS } from '../constants'

export type ColorSlot = 1 | 2 | 3 | 4 | 5

export type ColumnKey = 'requestedPrice' | GuidanceType | ApprovalType

export type DynamicColorMap = Partial<Record<ColumnKey, ColorSlot>>

const ALL_SLOTS: ColorSlot[] = [1, 2, 3, 4, 5]

export const assignColorSlot = (map: DynamicColorMap, key: ColumnKey): DynamicColorMap => {
  if (map[key] !== undefined) return map

  const usedSlots = new Set(Object.values(map))
  const freeSlot = ALL_SLOTS.find((slot) => !usedSlots.has(slot))

  if (freeSlot === undefined) return map

  return { ...map, [key]: freeSlot }
}

export const freeColorSlot = (map: DynamicColorMap, key: ColumnKey): DynamicColorMap => {
  if (map[key] === undefined) return map

  const { [key]: _removed, ...rest } = map
  return rest
}

export const resolveColorMap = (map: DynamicColorMap): Partial<Record<ColumnKey, string>> => {
  const resolved: Partial<Record<ColumnKey, string>> = {}

  for (const [key, slot] of Object.entries(map) as [ColumnKey, ColorSlot][]) {
    resolved[key] = DYNAMIC_COLORS[slot]
  }

  return resolved
}

export const isColorLimitReached = (map: DynamicColorMap): boolean =>
  Object.keys(map).length >= MAX_DYNAMIC_COLUMNS

const APPROVAL_METRIC_SUFFIXES = ['-ogm-percent', '-bd-net', '-total-discount-percent'] as const

const COL_ID_PREFIX_TO_KEY: [string, ColumnKey][] = [['requested-', 'requestedPrice']]

const FIELD_TO_KEY: Partial<Record<string, ColumnKey>> = {
  expertDiscountPercentage: 'expert',
  typicalDiscountPercentage: 'typical',
  floorDiscountPercentage: 'floor',
  calcGuidanceExpertPercentage: 'expert',
  calcGuidanceTypicalPercentage: 'typical',
  calcGuidanceFloorPercentage: 'floor',
  historicalTop25DiscountPercentage: 'top25',
  historicalMedianDiscountPercentage: 'median',
  historicalBottom25DiscountPercentage: 'bottom25',
}

const getColumnKeyForColDef = (col: ColDef): ColumnKey | undefined => {
  if (col.colId) {
    // Dynamic approval columns: approval-{compositeId}-{metric}
    // Extract the composite id (e.g. '0-ES18') as the color map key
    if (col.colId.startsWith('approval-')) {
      const withoutPrefix = col.colId.slice('approval-'.length)
      const matchedSuffix = APPROVAL_METRIC_SUFFIXES.find((suffix) =>
        withoutPrefix.endsWith(suffix)
      )
      if (matchedSuffix) {
        return withoutPrefix.slice(0, withoutPrefix.length - matchedSuffix.length) as ColumnKey
      }
    }

    const match = COL_ID_PREFIX_TO_KEY.find(([prefix]) => col.colId!.startsWith(prefix))
    if (match) return match[1]
  }

  if (col.field) {
    return FIELD_TO_KEY[col.field]
  }

  return undefined
}

export const applyDynamicColumnColor = <T>(
  col: ColDef<T>,
  colorMap: Partial<Record<ColumnKey, string>>
): ColDef<T> => {
  const key = getColumnKeyForColDef(col)
  if (!key) return col

  const color = colorMap[key]
  if (!color) return col

  return {
    ...col,
    headerClass: `dynamic-col-${key}`,
    cellStyle: (params: CellClassParams<T>) =>
      params.node?.rowPinned ? null : { backgroundColor: color },
  }
}

export const buildDynamicHeaderStyles = (colorMap: Partial<Record<ColumnKey, string>>): string =>
  Object.entries(colorMap)
    .map(([key, color]) => `.dynamic-col-${key} { background-color: ${color}; }`)
    .join('\n')
